import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { 
  Newspaper, 
  Plus, 
  Search, 
  RefreshCw,
  Edit,
  Trash2,
  Eye,
  Calendar,
  X,
  Image,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Check,
  Wand2,
  Clock,
  Send,
  Upload,
  Save,
  Loader,
  Play,
  Globe,
  Sparkles,
  Settings,
  Square,
  Power
} from 'lucide-react';
import './News.css';

const NEWS_PAGE_SIZE = 100;

function News() {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMoreNews, setHasMoreNews] = useState(true);
  const [newsPage, setNewsPage] = useState(0);
  const [totalNews, setTotalNews] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [langFilter, setLangFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDirectSaveModal, setShowDirectSaveModal] = useState(false);
  const [showParserModal, setShowParserModal] = useState(false);
  const [showArizonaModal, setShowArizonaModal] = useState(false);
  const [arizonaSettings, setArizonaSettings] = useState(null);
  const [timerSettings, setTimerSettings] = useState({ t1_hours: 36, t2_hours: 24, t3_hours: 8 });
  const [arizonaSaving, setArizonaSaving] = useState(false);
  const [arizonaStatus, setArizonaStatus] = useState('');
  const [arizonaTab, setArizonaTab] = useState('settings');
  const [arizonaPrompts, setArizonaPrompts] = useState({ prompt_1_2: '', prompt_2: '', support_chat: '' });
  const [miniappSize, setMiniappSize] = useState({ screenWidth: 0, screenHeight: 0 });
  const [parserStatus, setParserStatus] = useState(null);
  const [parserLogs, setParserLogs] = useState([]);
  const [autoParserSettings, setAutoParserSettings] = useState(null);
  const [parserRunning, setParserRunning] = useState(false);
  const [currentNews, setCurrentNews] = useState(null);
  const [arizonaFiles, setArizonaFiles] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [processing, setProcessing] = useState(false);
  const [processProgress, setProcessProgress] = useState({ percent: 0, message: '' });
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const loadMoreRef = useRef(null);
  const newsRequestIdRef = useRef(0);
  
  // Edit fields
  const [editTitle, setEditTitle] = useState('');
  const [editArticle, setEditArticle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [originalArticle, setOriginalArticle] = useState('');
  
  // Edit fields for published news (Russian)
  const [editTitleRu, setEditTitleRu] = useState('');
  const [editArticleRu, setEditArticleRu] = useState('');
  const [originalTitleRu, setOriginalTitleRu] = useState('');
  const [originalArticleRu, setOriginalArticleRu] = useState('');
  const [savingRu, setSavingRu] = useState(false);
  
  // Photos dirty tracking (для кнопки "Сохранить фото")
  const [photosDirty, setPhotosDirty] = useState(false);
  const [savingPhotos, setSavingPhotos] = useState(false);

  // Photo size detection (для подсветки дубликатов и автодедупа)
  // Map: photoNum -> { size: bytes|null, loading: bool }
  const [photoSizes, setPhotoSizes] = useState({});
  const [dedupingPhotos, setDedupingPhotos] = useState(false);
  const [deletingAllPhotos, setDeletingAllPhotos] = useState(false);

  // Detect changes
  const hasChanges = editTitle !== originalTitle || editArticle !== originalArticle;
  const hasChangesRu = editTitleRu !== originalTitleRu || editArticleRu !== originalArticleRu;
  
  // Create form
  const [createForm, setCreateForm] = useState({
    lang: 'en',
    title: '',
    article: '',
    sourceUrl: '',
    category: '',
    date: ''
  });
  const [createPhotoPreviews, setCreatePhotoPreviews] = useState([null, null, null]);
  const [createPhotoFiles, setCreatePhotoFiles] = useState([null, null, null]);
  
  // Arizona form helpers
  const rgbToStr = (arr) => Array.isArray(arr) ? arr.join(',') : String(arr || '');
  const strToRgb = (s) => String(s).split(',').map(n => parseInt(n.trim())).filter(n => !isNaN(n));
  const fzToStr = (arr) => Array.isArray(arr) ? arr.map(z => z.join(',')).join(';') : String(arr || '');
  const strToFz = (s) => String(s).split(';').map(p => p.split(',').map(n => parseInt(n.trim()))).filter(z => z.length === 2 && !isNaN(z[0]));

  // Direct save form
  const [directForm, setDirectForm] = useState({
    title: '',
    article: '',
    category: '',
    sourceUrl: ''
  });

  useEffect(() => {
    loadNews({ reset: true });
  }, [statusFilter, langFilter, searchQuery]);

  useEffect(() => {
    if (loading || loadingMore || !hasMoreNews || news.length === 0) return;

    const target = loadMoreRef.current;
    if (!target) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          observer.disconnect();
          loadNews({ reset: false });
        }
      },
      { rootMargin: '300px 0px' }
    );

    observer.observe(target);
    return () => observer.disconnect();
  }, [news.length, loading, loadingMore, hasMoreNews, newsPage]);

  // Load Arizona settings when modal opens
  const openArizonaModal = async () => {
    setShowArizonaModal(true);
    setArizonaStatus('');
    setArizonaTab('settings');
    try {
      const [azRes, timerRes, webRes] = await Promise.all([
        api.get('/api/admin/arizona-settings'),
        api.get('/api/admin/timer-settings'),
        api.get('/api/admin/website/settings')
      ]);
      if (azRes.data.success) setArizonaSettings(azRes.data.settings);
      if (timerRes.data.success) setTimerSettings(timerRes.data.settings);
      if (webRes.data.success) setMiniappSize(webRes.data.settings);
      
      // Load prompts separately so settings still show if prompts fail
      try {
        const promptsRes = await api.get('/api/admin/arizona-prompts');
        if (promptsRes.data.success) {
          setArizonaPrompts(promptsRes.data.prompts);
          if (!promptsRes.data.prompts.prompt_1_2 && !promptsRes.data.prompts.prompt_2) {
            console.warn('Arizona prompts are empty — files may not exist on server');
          }
        }
      } catch (promptErr) {
        console.error('Failed to load Arizona prompts:', promptErr);
        setArizonaStatus('⚠️ Промпты не загружены (файлы не найдены на сервере)');
      }
    } catch (e) {
      console.error('Failed to load Arizona settings:', e);
      setArizonaStatus('Ошибка загрузки настроек');
    }
  };

  const saveArizonaSettings = async () => {
    setArizonaSaving(true);
    setArizonaStatus('');
    try {
      const [azRes, timerRes, promptsRes, webRes] = await Promise.all([
        api.put('/api/admin/arizona-settings', arizonaSettings),
        api.put('/api/admin/timer-settings', timerSettings),
        api.put('/api/admin/arizona-prompts', arizonaPrompts),
        api.post('/api/admin/website/settings', miniappSize)
      ]);
      if (azRes.data.success && timerRes.data.success && promptsRes.data.success && webRes.data.success) {
        setArizonaStatus('✅ Настройки + промпты + размер сохранены');
        setTimeout(() => setArizonaStatus(''), 4000);
      } else {
        setArizonaStatus('❌ ' + (azRes.data.error || timerRes.data.error || promptsRes.data.error || webRes.data.error || 'Ошибка'));
      }
    } catch (e) {
      setArizonaStatus('❌ Ошибка: ' + e.message);
    } finally {
      setArizonaSaving(false);
    }
  };

  const updateAz = (key, value) => {
    setArizonaSettings(prev => ({ ...prev, [key]: value }));
  };

  // Poll progress when processing
  useEffect(() => {
    if (!processing || !currentNews) return;
    
    let cancelled = false;
    
    const poll = async () => {
      if (!currentNews || cancelled) return;
      try {
        // Poll progress
        const progressRes = await api.get(`/api/admin/news/${currentNews.id}/progress`);
        if (progressRes.data.success) {
          setProcessProgress({
            percent: progressRes.data.percent || 0,
            message: progressRes.data.message || ''
          });
          
          // If complete - refresh news data
          if (progressRes.data.complete || progressRes.data.percent >= 100) {
            const newsRes = await api.get(`/api/admin/news/${currentNews.id}`);
            if (newsRes.data.success) {
              setCurrentNews(newsRes.data.news);
              setArizonaFiles(newsRes.data.arizona_files);
            }
            setProcessing(false);
            setProcessProgress({ percent: 0, message: '' });
            loadNews();
            return;
          }
          
          // If error - stop polling
          if (progressRes.data.error) {
            const errorMessage = progressRes.data.message || 'Ошибка AI';
            setProcessing(false);
            setProcessProgress({ percent: 0, message: '' });
            alert(errorMessage);
            return;
          }
        }
        
        // Also check if news is processed
        const res = await api.get(`/api/admin/news/${currentNews.id}`);
        if (res.data.success) {
          const newsData = res.data.news;
          setCurrentNews(newsData);
          setArizonaFiles(res.data.arizona_files);
          
          // Stop polling if news is fully processed (ready/posted with ai_processed)
          if (newsData?.ai_processed && ['ready', 'posted', 'published'].includes(newsData?.status)) {
            setProcessing(false);
            setProcessProgress({ percent: 0, message: '' });
            loadNews();
            return;
          }
        }
      } catch (e) { console.error(e); }
      
      if (!cancelled) {
        setTimeout(poll, 2000);
      }
    };
    
    setTimeout(poll, 1000);
    
    return () => { cancelled = true; };
  }, [processing, currentNews?.id]);

  // Poll parser logs and status when modal is open
  useEffect(() => {
    if (!showParserModal) return;

    // Initial load
    loadParserLogs();
    loadParserStatus();

    // Poll every 2 seconds
    const interval = setInterval(() => {
      loadParserLogs();
      loadParserStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, [showParserModal]);

  // When currentNews changes — measure photo sizes for dup-highlight on the client
  useEffect(() => {
    if (!currentNews?.id) return;
    setPhotoSizes({});
    // Fire and forget; CORS may block some — fallback handled inside
    measurePhotoSizes(currentNews);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentNews?.id,
    currentNews?.photo_1, currentNews?.photo_2, currentNews?.photo_3, currentNews?.photo_4, currentNews?.photo_5,
    currentNews?.photo_6, currentNews?.photo_7, currentNews?.photo_8, currentNews?.photo_9, currentNews?.photo_10
  ]);

  // Parser functions
  const loadParserStatus = async () => {
    try {
      const [statusRes, runningRes] = await Promise.all([
        api.get('/api/admin/parser/status'),
        api.get('/api/admin/parser/running')
      ]);
      if (statusRes.data.success) {
        setParserStatus(statusRes.data.status);
      }
      if (runningRes.data.success) {
        setParserRunning(runningRes.data.running);
      }
    } catch (error) {
      console.error('Parser status error:', error);
    }
  };

  const loadParserLogs = async () => {
    try {
      const res = await api.get('/api/admin/parser/logs?limit=20');
      if (res.data.success) {
        setParserLogs(res.data.logs || []);
      }
    } catch (error) {
      console.error('Parser logs error:', error);
    }
  };

  const loadAutoParserSettings = async () => {
    try {
      const res = await api.get('/api/admin/parser/auto-settings');
      if (res.data.success) {
        setAutoParserSettings(res.data.settings);
      }
    } catch (error) {
      console.error('Auto parser settings error:', error);
    }
  };

  const openParserModal = () => {
    setShowParserModal(true);
    loadParserStatus();
    loadParserLogs();
    loadAutoParserSettings();
  };

  const startParser = async () => {
    if (!window.confirm('Запустить парсер новостей? Будет найдена 1 новость.')) return;
    try {
      const res = await api.post('/api/admin/parser/start', { manual: true, limit: 1 });
      if (res.data.success) {
        alert('Парсер запущен! Языки: ' + res.data.languages?.join(', '));
        loadParserStatus();
        loadParserLogs();
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const stopParser = async () => {
    if (!window.confirm('Остановить парсер?')) return;
    try {
      const res = await api.post('/api/admin/parser/stop');
      if (res.data.success) {
        alert('Парсер остановлен');
        loadParserStatus();
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const toggleAutoParser = async () => {
    try {
      const newState = !autoParserSettings?.autoEnabled;
      const currentTarget = autoParserSettings?.targetNewsCount || 30;
      const res = await api.post('/api/admin/parser/auto-settings', {
        autoEnabled: newState,
        intervalMinutes: 90,
        targetNewsCount: currentTarget
      });
      if (res.data.success) {
        loadAutoParserSettings();
        alert(newState ? `Автопарсинг включен! Поддержка стэка ${currentTarget} статей NEW` : 'Автопарсинг выключен');
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const updateStackSize = async (newSize) => {
    const size = parseInt(newSize, 10);
    if (!size || size < 5 || size > 400) {
      alert('Размер стэка должен быть от 5 до 400');
      return;
    }
    try {
      const res = await api.post('/api/admin/parser/auto-settings', {
        autoEnabled: autoParserSettings?.autoEnabled ?? false,
        intervalMinutes: 90,
        targetNewsCount: size
      });
      if (res.data.success) {
        loadAutoParserSettings();
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const clearParserLogs = async () => {
    if (!window.confirm('Очистить все логи парсера?')) return;
    try {
      const res = await api.post('/api/admin/parser/logs/clear');
      if (res.data.success) {
        loadParserLogs();
      } else {
        alert('Ошибка: ' + res.data.error);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const loadNews = async ({ reset = true } = {}) => {
    if (!reset && (loading || loadingMore || !hasMoreNews)) {
      return;
    }

    const requestId = reset ? ++newsRequestIdRef.current : newsRequestIdRef.current;
    const nextPage = reset ? 1 : newsPage + 1;

    if (reset) {
      setLoading(true);
      setLoadingMore(false);
      setHasMoreNews(true);
      setNewsPage(0);
    } else {
      setLoadingMore(true);
    }

    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (langFilter) params.append('lang', langFilter);
      if (searchQuery.trim()) params.append('search', searchQuery.trim());
      params.append('page', String(nextPage));
      params.append('limit', String(NEWS_PAGE_SIZE));

      const response = await api.get(`/api/admin/news?${params.toString()}`);
      if (requestId !== newsRequestIdRef.current) {
        return;
      }

      if (response.data.success) {
        const loadedNews = response.data.news || [];
        const pagination = response.data.pagination || {};

        setNews((prev) => {
          if (reset) return loadedNews;

          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueItems = loadedNews.filter((item) => !existingIds.has(item.id));
          return [...prev, ...uniqueItems];
        });
        setNewsPage(pagination.page || nextPage);
        setTotalNews(pagination.total ?? loadedNews.length);
        setHasMoreNews(Boolean(pagination.hasMore));
      }
    } catch (error) {
      console.error('Ошибка загрузки новостей:', error);
    } finally {
      if (requestId !== newsRequestIdRef.current) {
        return;
      }

      if (reset) {
        setLoading(false);
      } else {
        setLoadingMore(false);
      }
    }
  };

  const viewNews = async (id) => {
    try {
      const response = await api.get(`/api/admin/news/${id}`);
      if (response.data.success) {
        setCurrentNews(response.data.news);
        setArizonaFiles(response.data.arizona_files || null);
        setEditTitle(response.data.news.source_title || '');
        setEditArticle(response.data.news.source_article || '');
        setOriginalTitle(response.data.news.source_title || '');
        setOriginalArticle(response.data.news.source_article || '');
        setPhotosDirty(false);
        
        // Initialize Russian edit fields for published news
        setEditTitleRu(response.data.news.title_ru || '');
        setEditArticleRu(response.data.news.article_ru || '');
        setOriginalTitleRu(response.data.news.title_ru || '');
        setOriginalArticleRu(response.data.news.article_ru || '');
        
        // Check if there's ongoing processing for this news
        try {
          const progressRes = await api.get(`/api/admin/news/${id}/progress`);
          if (progressRes.data.success && progressRes.data.processing) {
            setProcessing(true);
            setProcessProgress({
              percent: progressRes.data.percent || 0,
              message: progressRes.data.message || ''
            });
          } else {
            setProcessing(false);
            setProcessProgress({ percent: 0, message: '' });
          }
        } catch (e) {
          console.error('Progress check error:', e);
        }
        
        const idx = news.findIndex(n => n.id === id);
        setCurrentIndex(idx);
        setShowModal(true);
      }
    } catch (error) {
      alert('Ошибка загрузки новости: ' + error.message);
    }
  };

  const deleteNews = async (id) => {
    if (!window.confirm('Переместить новость в корзину? Парсер больше не будет её предлагать.')) return;
    try {
      const res = await api.delete(`/api/admin/news/${id}`);
      if (res.data.success) {
        if (showModal && currentNews?.id === id) {
          const remainingNews = news.filter(n => n.id !== id);
          if (remainingNews.length > 0) {
            const nextIndex = Math.min(currentIndex, remainingNews.length - 1);
            setNews(remainingNews);
            viewNews(remainingNews[nextIndex].id);
          } else {
            setShowModal(false);
          }
        }
        // Удаляем из локального состояния без перезагрузки (сохраняет позицию скролла)
        setNews(prev => prev.filter(item => item.id !== id));
        setTotalNews(prev => Math.max(0, prev - 1));
      } else {
        alert('Ошибка: ' + (res.data.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Ошибка перемещения в корзину:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  const restoreNews = async (id) => {
    if (!window.confirm('Восстановить новость из корзины?')) return;
    try {
      const res = await api.post(`/api/admin/news/${id}/restore`);
      if (res.data.success) {
        if (showModal && currentNews?.id === id) {
          setCurrentNews(prev => ({ ...prev, status: 'good' }));
        }
        loadNews();
      } else {
        alert('Ошибка восстановления: ' + (res.data.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Ошибка восстановления:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  const permanentDeleteNews = async (id) => {
    if (!window.confirm('❗️ УДАЛИТЬ НАВСЕГДА? Это действие нельзя отменить!')) return;
    try {
      const res = await api.delete(`/api/admin/news/${id}/permanent`);
      if (res.data.success) {
        if (showModal && currentNews?.id === id) {
          setShowModal(false);
        }
        setNews(prev => prev.filter(item => item.id !== id));
        setTotalNews(prev => Math.max(0, prev - 1));
      } else {
        alert('Ошибка: ' + (res.data.error || 'Неизвестная ошибка'));
      }
    } catch (error) {
      console.error('Ошибка полного удаления:', error);
      alert('Ошибка: ' + (error.response?.data?.error || error.message));
    }
  };

  const saveNewsEdits = async () => {
    if (!currentNews) return;
    setSaving(true);
    try {
      await api.put(`/api/admin/news/${currentNews.id}`, {
        source_title: editTitle.trim(),
        source_article: editArticle.trim()
      });
      // Update local state so hasChanges resets
      setOriginalTitle(editTitle.trim());
      setOriginalArticle(editArticle.trim());
      setCurrentNews(prev => ({
        ...prev,
        source_title: editTitle.trim(),
        source_article: editArticle.trim()
      }));
      loadNews();
    } catch (error) {
      alert('Ошибка сохранения: ' + (error.response?.data?.error || error.message));
    } finally {
      setSaving(false);
    }
  };

  // Save Russian text and translate to all languages
  const saveRussianEdits = async () => {
    if (!currentNews) return;
    setSavingRu(true);
    try {
      const res = await api.put(`/api/admin/news/${currentNews.id}/translate`, {
        title_ru: editTitleRu.trim(),
        article_ru: editArticleRu.trim()
      });
      if (res.data.success) {
        // Update local state
        setOriginalTitleRu(editTitleRu.trim());
        setOriginalArticleRu(editArticleRu.trim());
        setCurrentNews(prev => ({
          ...prev,
          title_ru: editTitleRu.trim(),
          article_ru: editArticleRu.trim(),
          last_edited_at: res.data.last_edited_at || new Date().toISOString()
        }));
        loadNews();
        alert('Сохранено и переведено на все языки!');
      }
    } catch (error) {
      alert('Ошибка сохранения: ' + (error.response?.data?.error || error.message));
    } finally {
      setSavingRu(false);
    }
  };

  const togglePhotoSelection = async (photoNum) => {
    if (!currentNews) return;
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/toggle-photo/${photoNum}`);
      if (res.data.success) {
        setCurrentNews(prev => ({
          ...prev,
          [`ai_selected_${photoNum}`]: res.data.selected ? 1 : 0
        }));
        setPhotosDirty(true);
      }
    } catch (error) {
      console.error('Toggle photo error:', error);
    }
  };

  const deletePhoto = async (photoNum) => {
    if (!currentNews) return;
    if (!window.confirm(`Удалить фото #${photoNum} из новости?`)) return;
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/delete-photo/${photoNum}`);
      if (res.data.success) {
        setCurrentNews(prev => ({
          ...prev,
          [`photo_${photoNum}`]: null,
          [`ai_selected_${photoNum}`]: 0
        }));
        setPhotoSizes(prev => {
          const next = { ...prev };
          delete next[photoNum];
          return next;
        });
      }
    } catch (error) {
      alert('Ошибка удаления фото: ' + (error?.response?.data?.error || error.message));
    }
  };

  const deleteAllPhotos = async () => {
    if (!currentNews) return;
    if (!window.confirm('Удалить ВСЕ фото из этой новости? Действие необратимо.')) return;
    setDeletingAllPhotos(true);
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/delete-all-photos`);
      if (res.data.success) {
        const cleared = {};
        for (let i = 1; i <= 10; i++) {
          cleared[`photo_${i}`] = null;
          cleared[`ai_selected_${i}`] = 0;
        }
        setCurrentNews(prev => ({ ...prev, ...cleared }));
        setPhotoSizes({});
        setPhotosDirty(false);
      }
    } catch (error) {
      alert('Ошибка: ' + (error?.response?.data?.error || error.message));
    } finally {
      setDeletingAllPhotos(false);
    }
  };

  const dedupePhotos = async () => {
    if (!currentNews) return;
    setDedupingPhotos(true);
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/dedupe-photos`);
      if (res.data.success) {
        // Reload news to reflect server-side cleanup
        await reloadCurrentNews();
        setPhotoSizes({}); // refetch on next render via effect
        const removed = res.data.removed || 0;
        if (removed > 0) {
          alert(`Удалено дубликатов: ${removed}\nГрупп одинакового размера: ${(res.data.groups || []).length}`);
        } else {
          alert('Дубликатов по размеру не найдено');
        }
      }
    } catch (error) {
      alert('Ошибка дедупликации: ' + (error?.response?.data?.error || error.message));
    } finally {
      setDedupingPhotos(false);
    }
  };

  // Fetch byte-size for each photo (HEAD then GET-fallback) — for client-side dup highlighting
  const measurePhotoSizes = async (newsObj) => {
    if (!newsObj) return;
    const tasks = [];
    for (let i = 1; i <= 10; i++) {
      const url = newsObj[`photo_${i}`];
      if (!url) continue;
      tasks.push((async () => {
        try {
          const head = await fetch(url, { method: 'HEAD', mode: 'cors' });
          const len = head.headers.get('content-length');
          if (len) return [i, parseInt(len, 10)];
        } catch (_) {}
        try {
          const get = await fetch(url, { method: 'GET', mode: 'cors' });
          const buf = await get.arrayBuffer();
          return [i, buf.byteLength];
        } catch (_) {
          return [i, null];
        }
      })());
    }
    const results = await Promise.all(tasks);
    setPhotoSizes(prev => {
      const next = { ...prev };
      for (const [i, size] of results) next[i] = { size };
      return next;
    });
  };

  const savePhotos = async () => {
    if (!currentNews) return;
    setSavingPhotos(true);
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/save-photos`);
      if (res.data.success) {
        setPhotosDirty(false);
        console.log(`Saved ${res.data.count} photos for news #${currentNews.id}`);
      }
    } catch (error) {
      console.error('Save photos error:', error);
      alert('Ошибка сохранения фото: ' + (error?.response?.data?.error || error.message));
    } finally {
      setSavingPhotos(false);
    }
  };

  const approveNews = async () => {
    if (!currentNews) return;
    setProcessing(true);
    try {
      // Save edits first
      await api.put(`/api/admin/news/${currentNews.id}`, {
        source_title: editTitle.trim(),
        source_article: editArticle.trim()
      });
      
      const res = await api.post(`/api/admin/news/${currentNews.id}/approve`);
      if (res.data.success) {
        // Update currentNews immediately for better UX
        setCurrentNews(prev => ({
          ...prev,
          status: 'good',
          t2_expires_at: res.data.t2ExpiresAt
        }));
        loadNews();
      } else {
        alert('Ошибка: ' + (res.data.error || 'Unknown'));
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    } finally {
      setProcessing(false);
    }
  };

  const setNewsStatus = async (newStatus) => {
    if (!currentNews) return;
    try {
      const res = await api.put(`/api/admin/news/${currentNews.id}`, { status: newStatus });
      if (res.data.success) {
        setCurrentNews(prev => ({ ...prev, status: newStatus }));
        loadNews();
      } else {
        alert('Ошибка: ' + (res.data.error || 'Unknown'));
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const processAI = async (reprocess = false) => {
    if (!currentNews) return;
    setProcessing(true);
    try {
      // Save edits first if not processed
      if (!currentNews.ai_processed) {
        await api.put(`/api/admin/news/${currentNews.id}`, {
          source_title: editTitle.trim(),
          source_article: editArticle.trim()
        });
      }
      
      // Reset ai_processed for reprocess
      if (reprocess) {
        await api.put(`/api/admin/news/${currentNews.id}`, { ai_processed: false });
      }
      
      const res = await api.post(`/api/admin/news/${currentNews.id}/process-ai`);
      if (res.data.success) {
        // Polling handled by useEffect when processing=true
      } else {
        alert('Ошибка: ' + (res.data.error || 'Unknown'));
        setProcessing(false);
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
      setProcessing(false);
    }
  };

  const publishNews = async () => {
    if (!currentNews) return;

    setPublishing(true);
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/publish`);
      if (res.data.success) {
        // Optimistic UI update
        setCurrentNews(prev => ({
          ...prev,
          status: 'published',
          published_at: new Date().toISOString()
        }));
        // Refresh from server to get final state (miniapp_photos, translations, etc.)
        try {
          await reloadCurrentNews();
        } catch (e) {
          console.warn('reloadCurrentNews after publish failed:', e);
        }
        // Refresh list in background
        loadNews();
      } else {
        alert('Ошибка: ' + (res.data.error || 'Unknown'));
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    } finally {
      setPublishing(false);
    }
  };

  const markBad = async () => {
    if (!currentNews) return;
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/bad`);
      if (res.data.success) {
        const remaining = news.filter(n => n.id !== currentNews.id);
        if (remaining.length > 0) {
          const nextIdx = Math.min(currentIndex, remaining.length - 1);
          setNews(remaining);
          viewNews(remaining[nextIdx].id);
        } else {
          setShowModal(false);
        }
        loadNews();
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const markMid = async () => {
    if (!currentNews) return;
    try {
      const res = await api.post(`/api/admin/news/${currentNews.id}/mid`);
      if (res.data.success) {
        const remaining = news.filter(n => n.id !== currentNews.id);
        if (remaining.length > 0) {
          const nextIdx = Math.min(currentIndex, remaining.length - 1);
          setNews(remaining);
          viewNews(remaining[nextIdx].id);
        } else {
          setShowModal(false);
        }
        loadNews();
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    }
  };

  const reloadCurrentNews = async () => {
    if (!currentNews) return;
    const res = await api.get(`/api/admin/news/${currentNews.id}`);
    if (res.data.success) {
      setCurrentNews(res.data.news);
      setArizonaFiles(res.data.arizona_files);
      setEditTitle(res.data.news.source_title || '');
      setEditArticle(res.data.news.source_article || '');
      setOriginalTitle(res.data.news.source_title || '');
      setOriginalArticle(res.data.news.source_article || '');
    }
  };

  const navigateNews = (direction) => {
    const newIndex = currentIndex + direction;
    if (newIndex >= 0 && newIndex < news.length) {
      viewNews(news[newIndex].id);
    }
  };

  const createNews = async () => {
    if (!createForm.title.trim() || !createForm.article.trim()) {
      alert('Заголовок и текст обязательны');
      return;
    }
    
    setSaving(true);
    try {
      // Upload photos first if any
      let comicUrl = null;
      const photoUrls = [];
      const filesToUpload = [];
      const fileSlots = [];

      createPhotoFiles.forEach((file, idx) => {
        if (file) {
          filesToUpload.push(file);
          fileSlots.push(idx);
        }
      });

      if (filesToUpload.length > 0) {
        const formData = new FormData();
        filesToUpload.forEach(f => formData.append('photos', f));
        const uploadRes = await api.post('/api/admin/upload-photos', formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        if (!uploadRes.data.success) throw new Error(uploadRes.data.error || 'Upload failed');
        
        uploadRes.data.photos.forEach((url, i) => {
          const slot = fileSlots[i];
          if (slot === 0) comicUrl = url;
          else photoUrls.push(url);
        });
      }

      const res = await api.post('/api/admin/news', {
        sourceLang: createForm.lang,
        sourceTitle: createForm.title.trim(),
        sourceArticle: createForm.article.trim(),
        sourceUrl: createForm.sourceUrl.trim() || undefined,
        publishedDate: createForm.date || null,
        category: createForm.category || undefined,
        manualComicUrl: comicUrl,
        photos: photoUrls.length > 0 ? photoUrls : undefined
      });
      
      if (res.data.success) {
        setShowCreateModal(false);
        setCreateForm({ lang: 'en', title: '', article: '', sourceUrl: '', category: '', date: '' });
        setCreatePhotoPreviews([null, null, null]);
        setCreatePhotoFiles([null, null, null]);
        loadNews();
        setTimeout(() => viewNews(res.data.newsId), 300);
      } else {
        if (res.data.error === 'duplicate_source_url') {
          alert(res.data.message || 'Новость с таким URL уже существует');
        } else {
          alert('Ошибка: ' + (res.data.error || 'Unknown'));
        }
      }
    } catch (error) {
      alert('Ошибка: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const getTimeLeft = (expiresAt) => {
    if (!expiresAt) return '';
    const diff = new Date(expiresAt) - new Date();
    if (diff <= 0) return 'Истекло';
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}ч ${minutes}м`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      'new': { label: 'NEW', className: 'badge-new' },
      'mid': { label: 'MID', className: 'badge-mid' },
      'good': { label: 'GOOD', className: 'badge-good' },
      'mid': { label: 'MID', className: 'badge-mid' },
      'processing': { label: '⚙️ ОБРАБОТКА', className: 'badge-processing' },
      'ready': { label: 'READY', className: 'badge-ready' },
      'moderation': { label: 'READY', className: 'badge-ready' },
      'posted': { label: 'POSTED', className: 'badge-posted' },
      'published': { label: 'POSTED', className: 'badge-posted' },
      'bad': { label: '🗑️ КОРЗИНА', className: 'badge-bad' }
    };
    return badges[status] || { label: status?.toUpperCase(), className: 'badge-default' };
  };

  const isProcessed = currentNews?.ai_processed;
  const isProcessing = currentNews?.status === 'processing';

  return (
    <div className="news-page fade-in">
      <div className="page-header">
        <h1 className="page-title">Новости</h1>
        <div className="header-actions">
          <button className="btn btn-secondary" onClick={openParserModal}>
            <Settings size={18} />
            Парсер
          </button>
          <button className="btn btn-arizona" onClick={openArizonaModal}>
            <Sparkles size={18} />
            Настройки скрипта
          </button>
          <button className="btn btn-secondary" onClick={loadNews}>
            <RefreshCw size={18} />
            Обновить
          </button>
          <button className="btn btn-secondary" onClick={() => setShowDirectSaveModal(true)}>
            <Save size={18} />
            💾 БД сохранение
          </button>
          <button className="btn btn-primary" onClick={() => {
            setCreateForm(f => ({ ...f, date: new Date().toISOString().slice(0, 16) }));
            setCreatePhotoPreviews([null, null, null]);
            setCreatePhotoFiles([null, null, null]);
            setShowCreateModal(true);
          }}>
            <Plus size={18} />
            + Новость вручную
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="news-filters">
        <div className="filter-group filter-search">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="input search-input"
            placeholder="Поиск по заголовку..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <label>Статус:</label>
          <select 
            className="input select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">Все</option>
            <option value="new">New</option>
            <option value="mid">Mid</option>
            <option value="good">Good</option>
            <option value="mid">Mid</option>
            <option value="processing">Processing</option>
            <option value="ready">Ready</option>
            <option value="posted">Posted</option>
            <option value="bad">🗑️ Корзина</option>
          </select>
        </div>
        <div className="filter-group">
          <label>Язык:</label>
          <select 
            className="input select"
            value={langFilter}
            onChange={(e) => setLangFilter(e.target.value)}
          >
            <option value="">Все</option>
            <option value="ru">RU</option>
            <option value="en">EN</option>
            <option value="es">ES</option>
            <option value="it">IT</option>
            <option value="de">DE</option>
          </select>
        </div>
        <div className="filter-count">
          {totalNews > 0 ? `Показано: ${news.length} из ${totalNews}` : `Найдено: ${news.length}`}
        </div>
      </div>

      {/* News List */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <span>Загрузка...</span>
        </div>
      ) : news.length === 0 ? (
        <div className="empty-state">
          <Newspaper size={48} className="empty-state-icon" />
          <h3 className="empty-state-title">Нет новостей</h3>
          <p className="empty-state-text">Добавьте первую новость</p>
        </div>
      ) : (
        <>
        <div className="news-list">
          {news.map((item) => {
            const status = getStatusBadge(item.status);
            const aiProcessed = !!item.ai_processed;
            const displayTitle = item.title_ru || item.processed_title || item.source_title || 'Без заголовка';
            
            // Timers
            let timer = null;
            if (item.status === 'new' && item.ai_process_at) {
              timer = { label: `T1 → good ${getTimeLeft(item.ai_process_at)}`, type: 't1' };
            } else if (item.status === 'good' && item.t2_expires_at) {
              timer = { label: `T2 → КомДел ${getTimeLeft(item.t2_expires_at)}`, type: 't2' };
            } else if (item.status === 'processing') {
              timer = { label: 'Обработка...', type: 'processing' };
            } else if ((item.status === 'ready' || item.status === 'moderation') && item.moderation_expires_at) {
              timer = { label: `T3 → posted ${getTimeLeft(item.moderation_expires_at)}`, type: 't3' };
            }
            
            return (
              <div key={item.id} className={`news-item ${aiProcessed ? 'processed' : ''}`}>
                <div className="news-item-content">
                  <div className="news-badges">
                    <span className={`badge ${status.className}`}>{status.label}</span>
                    <span className="badge badge-lang">{(item.source_lang || 'en').toUpperCase()}</span>
                    <span className={`badge ${aiProcessed ? 'badge-ai-done' : 'badge-ai-pending'}`}>
                      {aiProcessed ? '✅ AI' : '⏳ Не обработана'}
                    </span>
                    {timer && (
                      <span className={`badge badge-timer badge-timer-${timer.type}`}>
                        ⏱ {timer.label}
                      </span>
                    )}
                  </div>
                  <h3 className="news-item-title">{displayTitle}</h3>
                  <p className="news-item-meta">
                    #{item.article_number || item.id} • {item.source_name || 'Manual'} • {new Date(item.created_at).toLocaleString('ru-RU')}
                  </p>
                </div>
                <div className="news-item-actions">
                  <button className="btn btn-primary btn-sm" onClick={() => viewNews(item.id)}>
                    <Eye size={16} />
                    Просмотр
                  </button>
                  {item.status === 'bad' ? (
                    <>
                      <button className="btn btn-success btn-sm" onClick={() => restoreNews(item.id)} title="Восстановить">
                        <RefreshCw size={16} />
                      </button>
                      <button className="btn btn-danger btn-sm" onClick={() => permanentDeleteNews(item.id)} title="Удалить навсегда">
                        <Trash2 size={16} />
                      </button>
                    </>
                  ) : (
                    <button className="btn btn-warning btn-sm" onClick={() => deleteNews(item.id)} title="В корзину">
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
        <div ref={loadMoreRef} className="news-list-sentinel" aria-hidden="true" />
        {loadingMore && (
          <div className="news-list-status">
            <div className="news-list-spinner" />
            <span>Подгружаем ещё 100 новостей...</span>
          </div>
        )}
        {!hasMoreNews && news.length > 0 && (
          <div className="news-list-status news-list-status-done">
            Загружены все новости
          </div>
        )}
        </>
      )}

      {/* View/Edit Modal */}
      {showModal && currentNews && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <h2 className="modal-title">
                  Новость #{currentNews.article_number || currentNews.id}
                  {currentNews.extID && (
                    <span style={{ fontSize: '14px', color: '#888', marginLeft: '10px' }}>
                      Share: {currentNews.extID}
                    </span>
                  )}
                </h2>
                <div className="modal-nav">
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigateNews(-1)}
                    disabled={currentIndex <= 0}
                  >
                    <ChevronLeft size={18} />
                  </button>
                  <span className="nav-counter">{currentIndex + 1}/{news.length}</span>
                  <button 
                    className="btn btn-ghost btn-sm"
                    onClick={() => navigateNews(1)}
                    disabled={currentIndex >= news.length - 1}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <X size={20} />
              </button>
            </div>
            
            <div className="modal-body">
              {/* Status badges */}
              <div className="modal-badges">
                <span className={`badge ${getStatusBadge(currentNews.status).className}`}>
                  {getStatusBadge(currentNews.status).label}
                </span>
                <span className={`badge ${isProcessed ? 'badge-ai-done' : 'badge-ai-pending'}`}>
                  {isProcessed ? '✅ Обработана AI' : '⏳ Не обработана AI'}
                </span>
                <span className="badge badge-lang">
                  {(currentNews.source_lang || 'en').toUpperCase()}
                </span>
                {currentNews.status === 'new' && currentNews.ai_process_at && (
                  <span className="badge badge-timer badge-timer-t1">
                    ⏱ T1 → good {getTimeLeft(currentNews.ai_process_at)}
                  </span>
                )}
                {currentNews.status === 'good' && currentNews.t2_expires_at && (
                  <span className="badge badge-timer badge-timer-t2">
                    ⏱ T2 → КомДел {getTimeLeft(currentNews.t2_expires_at)}
                  </span>
                )}
                {isProcessing && (
                  <span className="badge badge-timer badge-timer-processing">
                    ⚙️ Обработка AI...
                  </span>
                )}
                {(currentNews.status === 'ready' || currentNews.status === 'moderation') && currentNews.moderation_expires_at && (
                  <span className="badge badge-timer badge-timer-t3">
                    ⏱ T3 → posted {getTimeLeft(currentNews.moderation_expires_at)}
                  </span>
                )}
              </div>
              
              <div className="modal-meta">
                <strong>Источник:</strong>{' '}
                {currentNews.source_url && !currentNews.source_url.startsWith('manual-') ? (
                  <a 
                    href={currentNews.source_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="source-link"
                    title={currentNews.source_url}
                  >
                    {currentNews.source_url.length > 60 
                      ? currentNews.source_url.substring(0, 60) + '...' 
                      : currentNews.source_url}
                  </a>
                ) : (
                  currentNews.source_name || 'Manual'
                )} | 
                <strong> Символов:</strong> {(currentNews.source_article || '').length} | 
                <strong> Создана:</strong> {new Date(currentNews.created_at).toLocaleString('ru-RU')}
              </div>

              {!isProcessed ? (
                /* Editable content for unprocessed */
                <div className="edit-section">
                  <div className="input-group">
                    <label className="input-label">Заголовок</label>
                    <input
                      type="text"
                      className="input input-highlight"
                      value={editTitle}
                      onChange={(e) => setEditTitle(e.target.value)}
                      placeholder="Заголовок новости"
                    />
                  </div>
                  <div className="input-group">
                    <label className="input-label">Текст статьи</label>
                    {currentNews.source_article_ru && currentNews.source_lang && currentNews.source_lang.toLowerCase() !== 'ru' && (
                      <div style={{
                        background: 'rgba(255,255,255,0.05)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: '8px',
                        padding: '10px 14px',
                        marginBottom: '8px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        fontStyle: 'italic',
                        fontSize: '13px',
                        lineHeight: '1.5',
                        color: 'rgba(255,255,255,0.7)',
                        whiteSpace: 'pre-wrap'
                      }}>
                        <span style={{ fontWeight: 600, fontStyle: 'normal', color: 'rgba(255,255,255,0.5)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                          Превью (RU)
                        </span>
                        <br />
                        {currentNews.source_article_ru}
                      </div>
                    )}
                    <textarea
                      className="input textarea input-highlight"
                      value={editArticle}
                      onChange={(e) => setEditArticle(e.target.value)}
                      placeholder="Текст новости..."
                      rows={12}
                    />
                  </div>
                  
                  {/* Photos from parser for selection */}
                  {[1,2,3,4,5,6,7,8,9,10].some(i => currentNews[`photo_${i}`]) && (
                    <div className="photos-section">
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                        <label className="input-label" style={{ margin: 0 }}>Фотографии из источника (выбор для миниаппа)</label>
                        <div style={{ display:'flex', gap:'0.5rem' }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={dedupePhotos}
                            disabled={dedupingPhotos}
                            title="Удалить дубликаты по размеру (одинаковые байты = одна фотка)"
                          >
                            {dedupingPhotos ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
                            {' '}Убрать дубли по размеру
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={deleteAllPhotos}
                            disabled={deletingAllPhotos}
                            title="Удалить все фото из новости"
                          >
                            {deletingAllPhotos ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                            {' '}Удалить все фото
                          </button>
                        </div>
                      </div>
                      <div className="photos-grid">
                        {(() => {
                          // Group photos by detected size for visual dup-highlight
                          const sizeGroups = {};
                          for (let i = 1; i <= 10; i++) {
                            const url = currentNews[`photo_${i}`];
                            const sz = photoSizes[i]?.size;
                            if (!url || sz == null) continue;
                            const k = String(sz);
                            (sizeGroups[k] = sizeGroups[k] || []).push(i);
                          }
                          return [1,2,3,4,5,6,7,8,9,10].map(i => {
                            const photo = currentNews[`photo_${i}`];
                            const selected = currentNews[`ai_selected_${i}`];
                            if (!photo) return null;
                            const size = photoSizes[i]?.size;
                            const grp = size != null ? sizeGroups[String(size)] : null;
                            const isDup = grp && grp.length > 1 && grp[0] !== i;
                            const dupLabel = grp && grp.length > 1
                              ? (isDup ? `дубль слота #${grp[0]}` : `оригинал (${grp.length} копий)`)
                              : null;
                            const sizeStr = size != null
                              ? (size >= 1024*1024 ? (size/1024/1024).toFixed(2)+' МБ' : Math.round(size/1024)+' КБ')
                              : '…';
                            return (
                              <div
                                key={i}
                                className={`photo-item ${selected ? 'selected' : ''} ${isDup ? 'photo-dup' : ''}`}
                                style={isDup ? { outline:'2px dashed #ff9800', opacity:0.7 } : undefined}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); deletePhoto(i); }}
                                  title="Удалить эту фотку"
                                  style={{
                                    position:'absolute', top:4, right:4, zIndex:2,
                                    background:'rgba(220,38,38,0.9)', color:'#fff', border:'none',
                                    width:24, height:24, borderRadius:'50%', cursor:'pointer',
                                    display:'flex', alignItems:'center', justifyContent:'center', padding:0
                                  }}
                                >
                                  <X size={14} />
                                </button>
                                <img
                                  src={photo}
                                  alt={`Photo ${i}`}
                                  onClick={() => togglePhotoSelection(i)}
                                  style={{ cursor:'pointer' }}
                                />
                                <div
                                  className="photo-checkbox"
                                  onClick={() => togglePhotoSelection(i)}
                                  style={{ cursor:'pointer' }}
                                >
                                  {selected ? '✓' : ''}
                                </div>
                                <div style={{
                                  position:'absolute', bottom:4, left:4, zIndex:1,
                                  background:'rgba(0,0,0,0.65)', color:'#fff',
                                  fontSize:10, padding:'2px 6px', borderRadius:4
                                }}>
                                  #{i} · {sizeStr}{dupLabel ? ` · ${dupLabel}` : ''}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      {photosDirty && (
                        <button
                          className="btn btn-success"
                          onClick={savePhotos}
                          disabled={savingPhotos}
                          style={{ marginTop: '0.75rem', animation: 'fadeIn 0.3s ease' }}
                        >
                          {savingPhotos ? <Loader size={16} className="spin" /> : <Save size={16} />}
                          {savingPhotos ? ' Сохранение...' : ' Сохранить выбор фото'}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                /* Processed content - show Arizona output */
                <div className="processed-section">
                  {/* Editable Russian title and article */}
                  <div className="edit-section" style={{ marginBottom: '1.5rem' }}>
                    <div className="input-group">
                      <label className="input-label">Заголовок (RU) — редактируемый</label>
                      <input
                        type="text"
                        className="input input-highlight"
                        value={editTitleRu}
                        onChange={(e) => setEditTitleRu(e.target.value)}
                        placeholder="Заголовок на русском"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Текст статьи (RU) — редактируемый</label>
                      <textarea
                        className="input textarea input-highlight"
                        value={editArticleRu}
                        onChange={(e) => setEditArticleRu(e.target.value)}
                        placeholder="Текст статьи на русском..."
                        rows={8}
                      />
                    </div>
                    {hasChangesRu && (
                      <button
                        className="btn btn-primary"
                        onClick={saveRussianEdits}
                        disabled={savingRu}
                        style={{ marginTop: '0.75rem' }}
                      >
                        {savingRu ? 'Сохранение и перевод...' : '💾 Сохранить и перевести на все языки'}
                      </button>
                    )}
                    {currentNews.last_edited_at && (
                      <p style={{ fontSize: '12px', color: '#888', marginTop: '0.5rem' }}>
                        Последнее изменение: {new Date(currentNews.last_edited_at).toLocaleString('ru-RU')}
                      </p>
                    )}
                  </div>

                  {/* Photos from parser — редактирование выбора фото и для processed (включая posted) */}
                  {[1,2,3,4,5,6,7,8,9,10].some(i => currentNews[`photo_${i}`]) && (
                    <div className="photos-section" style={{ marginBottom: '1.5rem' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'0.5rem' }}>
                        <label className="input-label" style={{ margin: 0 }}>Фотографии из источника (выбор для миниаппа)</label>
                        <div style={{ display:'flex', gap:'0.5rem' }}>
                          <button
                            type="button"
                            className="btn btn-sm"
                            onClick={dedupePhotos}
                            disabled={dedupingPhotos}
                            title="Удалить дубликаты по размеру (одинаковые байты = одна фотка)"
                          >
                            {dedupingPhotos ? <Loader size={14} className="spin" /> : <Sparkles size={14} />}
                            {' '}Убрать дубли по размеру
                          </button>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={deleteAllPhotos}
                            disabled={deletingAllPhotos}
                            title="Удалить все фото из новости"
                          >
                            {deletingAllPhotos ? <Loader size={14} className="spin" /> : <Trash2 size={14} />}
                            {' '}Удалить все фото
                          </button>
                        </div>
                      </div>
                      <div className="photos-grid">
                        {(() => {
                          const sizeGroups = {};
                          for (let i = 1; i <= 10; i++) {
                            const url = currentNews[`photo_${i}`];
                            const sz = photoSizes[i]?.size;
                            if (!url || sz == null) continue;
                            const k = String(sz);
                            (sizeGroups[k] = sizeGroups[k] || []).push(i);
                          }
                          return [1,2,3,4,5,6,7,8,9,10].map(i => {
                            const photo = currentNews[`photo_${i}`];
                            const selected = currentNews[`ai_selected_${i}`];
                            if (!photo) return null;
                            const size = photoSizes[i]?.size;
                            const grp = size != null ? sizeGroups[String(size)] : null;
                            const isDup = grp && grp.length > 1 && grp[0] !== i;
                            const dupLabel = grp && grp.length > 1
                              ? (isDup ? `дубль слота #${grp[0]}` : `оригинал (${grp.length} копий)`)
                              : null;
                            const sizeStr = size != null
                              ? (size >= 1024*1024 ? (size/1024/1024).toFixed(2)+' МБ' : Math.round(size/1024)+' КБ')
                              : '…';
                            return (
                              <div
                                key={i}
                                className={`photo-item ${selected ? 'selected' : ''} ${isDup ? 'photo-dup' : ''}`}
                                style={isDup ? { outline:'2px dashed #ff9800', opacity:0.7 } : undefined}
                              >
                                <button
                                  type="button"
                                  onClick={(e) => { e.stopPropagation(); deletePhoto(i); }}
                                  title="Удалить эту фотку"
                                  style={{
                                    position:'absolute', top:4, right:4, zIndex:2,
                                    background:'rgba(220,38,38,0.9)', color:'#fff', border:'none',
                                    width:24, height:24, borderRadius:'50%', cursor:'pointer',
                                    display:'flex', alignItems:'center', justifyContent:'center', padding:0
                                  }}
                                >
                                  <X size={14} />
                                </button>
                                <img
                                  src={photo}
                                  alt={`Photo ${i}`}
                                  onClick={() => togglePhotoSelection(i)}
                                  style={{ cursor:'pointer' }}
                                />
                                <div
                                  className="photo-checkbox"
                                  onClick={() => togglePhotoSelection(i)}
                                  style={{ cursor:'pointer' }}
                                >
                                  {selected ? '✓' : ''}
                                </div>
                                <div style={{
                                  position:'absolute', bottom:4, left:4, zIndex:1,
                                  background:'rgba(0,0,0,0.65)', color:'#fff',
                                  fontSize:10, padding:'2px 6px', borderRadius:4
                                }}>
                                  #{i} · {sizeStr}{dupLabel ? ` · ${dupLabel}` : ''}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                      {photosDirty && (
                        <button
                          className="btn btn-success"
                          onClick={savePhotos}
                          disabled={savingPhotos}
                          style={{ marginTop: '0.75rem', animation: 'fadeIn 0.3s ease' }}
                        >
                          {savingPhotos ? <Loader size={16} className="spin" /> : <Save size={16} />}
                          {savingPhotos ? ' Сохранение...' : ' Сохранить выбор фото'}
                        </button>
                      )}
                    </div>
                  )}

                  {/* Arizona Output */}
                  <div className="arizona-section">
                    <h4>
                      <Sparkles size={18} />
                      Arizona Output 
                      <span className="arizona-subtitle">(1 base + 6 баблов + 6 комиксов)</span>
                    </h4>
                    
                    {currentNews.comic_image || arizonaFiles?.master ? (
                      <div className="arizona-content">
                        {/* 1. Base image - без баблов */}
                        <div className="arizona-group">
                          <p className="arizona-label">1. Base (без баблов)</p>
                          <div className="arizona-base">
                            <img 
                              src={arizonaFiles?.master || currentNews.comic_image} 
                              alt="Base comic"
                            />
                          </div>
                        </div>
                        
                        {/* 2. Баблы на пустом фоне */}
                        <div className="arizona-group">
                          <p className="arizona-label">2. Баблы на пустом фоне (7 языков)</p>
                          <div className="arizona-bubbles">
                            {['ru', 'en', 'es', 'it', 'de', 'jp', 'zh'].map(lang => {
                              const src = arizonaFiles?.bubbles?.[lang];
                              const flags = { ru: '🇷🇺', en: '🇬🇧', es: '🇪🇸', it: '🇮🇹', de: '🇩🇪', jp: '🇯🇵', zh: '🇨🇳' };
                              return (
                                <div key={lang} className="bubble-item">
                                  <span className="bubble-lang">{flags[lang]} {lang.toUpperCase()}</span>
                                  {src ? (
                                    <img src={src} alt={`Bubble ${lang}`} />
                                  ) : (
                                    <div className="bubble-empty">—</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                        
                        {/* 3. Комиксы с баблами - финальные изображения */}
                        <div className="arizona-group">
                          <p className="arizona-label">3. Комиксы с баблами (7 языков)</p>
                          <div className="arizona-combined">
                            {['ru', 'en', 'es', 'it', 'de', 'jp', 'zh'].map(lang => {
                              const src = arizonaFiles?.combined?.[lang] || currentNews['comic_image_' + lang];
                              const flags = { ru: '🇷🇺', en: '🇬🇧', es: '🇪🇸', it: '🇮🇹', de: '🇩🇪', jp: '🇯🇵', zh: '🇨🇳' };
                              return (
                                <div key={lang} className="combined-item">
                                  <span className="combined-lang">{flags[lang]} {lang.toUpperCase()}</span>
                                  {src ? (
                                    <img src={src} alt={`Comic ${lang}`} />
                                  ) : (
                                    <div className="combined-empty">—</div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="arizona-empty">
                        <Image size={32} />
                        <span>Комиксы ещё генерируются...</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              {/* Action buttons based on status */}
              {(currentNews.status === 'new' || currentNews.status === 'mid') && (
                <>
                  {hasChanges && (
                    <button
                      className="btn btn-primary"
                      onClick={saveNewsEdits}
                      disabled={saving}
                      style={{
                        animation: 'fadeIn 0.3s ease',
                      }}
                    >
                      {saving ? <Loader size={18} className="spin" /> : <Save size={18} />}
                      {saving ? 'Сохранение...' : 'Сохранить'}
                    </button>
                  )}
                  <button
                    className="btn btn-success"
                    onClick={approveNews}
                    disabled={processing}
                  >
                    {processing ? <Loader size={18} className="spin" /> : <Check size={18} />}
                    Одобрить → good
                  </button>
                  {currentNews.status === 'new' && (
                    <button
                      className="btn btn-secondary"
                      onClick={() => setNewsStatus('mid')}
                    >
                      <Clock size={18} />
                      Отложить → mid
                    </button>
                  )}
                  <button
                    className="btn btn-warning"
                    onClick={() => setNewsStatus('bad')}
                  >
                    <Trash2 size={18} />
                    В корзину → bad
                  </button>
                </>
              )}

              {(currentNews.status === 'good' || currentNews.status === 'mid') && (
                <button
                  className="btn btn-purple"
                  onClick={() => processAI(false)}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader size={18} className="spin" />
                      {processProgress.percent > 0 ? `${processProgress.percent}%` : 'Запуск...'}
                    </>
                  ) : (
                    <>
                      <Wand2 size={18} />
                      Отправить в КомДел
                    </>
                  )}
                </button>
              )}


              {isProcessing && (
                <span className="processing-badge">
                  <Loader size={18} className="spin" />
                  {processProgress.percent > 0 ? `${processProgress.percent}% - ${processProgress.message}` : 'Обработка идёт...'}
                </span>
              )}

              {(currentNews.status === 'ready' || currentNews.status === 'moderation') && (
                <>
                  <button
                    className="btn btn-success"
                    onClick={publishNews}
                    disabled={publishing}
                  >
                    {publishing ? (
                      <>
                        <Loader size={18} className="spin" />
                        Публикация...
                      </>
                    ) : (
                      <>
                        <Send size={18} />
                        Опубликовать
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-purple"
                    onClick={() => processAI(true)}
                    disabled={processing}
                  >
                    {processing ? (
                      <>
                        <Loader size={18} className="spin" />
                        {processProgress.percent > 0 ? `${processProgress.percent}%` : 'Запуск...'}
                      </>
                    ) : (
                      <>
                        <RefreshCw size={18} />
                        Переотправить в КомДел
                      </>
                    )}
                  </button>
                </>
              )}

              {(currentNews.status === 'posted' || currentNews.status === 'published') && (
                <button
                  className="btn btn-purple"
                  onClick={() => processAI(true)}
                  disabled={processing}
                >
                  {processing ? (
                    <>
                      <Loader size={18} className="spin" />
                      {processProgress.percent > 0 ? `${processProgress.percent}%` : 'Запуск...'}
                    </>
                  ) : (
                    <>
                      <RefreshCw size={18} />
                      Переотправить в КомДел
                    </>
                  )}
                </button>
              )}

              {/* Кнопка MID — доступна из любого статуса кроме bad/mid */}
              {currentNews.status !== 'bad' && currentNews.status !== 'mid' && (
                <button
                  className="btn btn-warning btn-sm"
                  onClick={markMid}
                  style={{ background: '#f59e0b', color: '#fff' }}
                >
                  MID
                </button>
              )}

              {/* Корзина / Восстановление */}
              {currentNews.status === 'bad' ? (
                <>
                  <button className="btn btn-success" onClick={() => restoreNews(currentNews.id)}>
                    <RefreshCw size={18} />
                    Восстановить
                  </button>
                  <button className="btn btn-danger" onClick={() => permanentDeleteNews(currentNews.id)}>
                    <Trash2 size={18} />
                    Удалить навсегда
                  </button>
                </>
              ) : (
                <button className="btn btn-danger" onClick={markBad}>
                  <Trash2 size={18} />
                  Удалить
                </button>
              )}
              
              <button className="btn btn-ghost" onClick={() => setShowModal(false)}>
                Закрыть
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create News Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">+ Новость вручную</h2>
              <button className="modal-close" onClick={() => setShowCreateModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="create-news-hint">Статья сохраняется как есть. AI обработка запускается из просмотра новости.</p>

              <div className="input-group">
                <label className="input-label">Заголовок *</label>
                <input
                  type="text"
                  className="input"
                  value={createForm.title}
                  onChange={(e) => setCreateForm({...createForm, title: e.target.value})}
                  placeholder="Заголовок новости"
                />
              </div>
              
              <div className="input-group">
                <label className="input-label">
                  Текст статьи * <span className="char-count">{createForm.article.length} символов</span>
                </label>
                <textarea
                  className="input textarea"
                  value={createForm.article}
                  onChange={(e) => setCreateForm({...createForm, article: e.target.value})}
                  placeholder="Полный текст статьи..."
                  rows={10}
                />
              </div>
              
              <div className="input-group">
                <label className="input-label">Ссылка на источник</label>
                <input
                  type="text"
                  className="input"
                  value={createForm.sourceUrl}
                  onChange={(e) => setCreateForm({...createForm, sourceUrl: e.target.value})}
                  placeholder="https://..."
                />
              </div>

              <div className="input-row input-row-3">
                <div className="input-group">
                  <label className="input-label">Язык</label>
                  <select 
                    className="input"
                    value={createForm.lang}
                    onChange={(e) => setCreateForm({...createForm, lang: e.target.value})}
                  >
                    <option value="en">English</option>
                    <option value="ru">Русский</option>
                    <option value="es">Español</option>
                    <option value="it">Italiano</option>
                    <option value="de">Deutsch</option>
                    <option value="ja">日本語</option>
                  </select>
                </div>
                <div className="input-group">
                  <label className="input-label">Дата</label>
                  <input
                    type="datetime-local"
                    className="input"
                    value={createForm.date}
                    onChange={(e) => setCreateForm({...createForm, date: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">Категория</label>
                  <select
                    className="input"
                    value={createForm.category}
                    onChange={(e) => setCreateForm({...createForm, category: e.target.value})}
                  >
                    <option value="">Без категории</option>
                    <option value="Премьера">Премьера</option>
                    <option value="Гастроли">Гастроли</option>
                    <option value="Назначения">Назначения</option>
                    <option value="Контракты">Контракты</option>
                    <option value="Рецензии">Рецензии</option>
                    <option value="Скандалы">Скандалы</option>
                    <option value="Инсайды">Инсайды</option>
                    <option value="Образование">Образование</option>
                    <option value="Интервью">Интервью</option>
                    <option value="Другое">Другое</option>
                  </select>
                </div>
              </div>

              {/* Photo upload */}
              <div className="input-group">
                <label className="input-label">Фотографии</label>
                <div className="create-photo-slots">
                  {['Комикс', 'Фото 2', 'Фото 3'].map((label, idx) => (
                    <div key={idx} className="create-photo-slot">
                      <div className="create-photo-slot-label">{label}</div>
                      <div 
                        className={`create-photo-box ${idx === 0 ? 'create-photo-box-comic' : ''}`}
                        onClick={() => document.getElementById(`createFileInput${idx}`).click()}
                      >
                        {createPhotoPreviews[idx] ? (
                          <img src={createPhotoPreviews[idx]} alt={label} className="create-photo-preview" />
                        ) : (
                          <span className="create-photo-placeholder">+ {label}</span>
                        )}
                        <input
                          type="file"
                          id={`createFileInput${idx}`}
                          accept="image/*"
                          style={{ display: 'none' }}
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (!file) return;
                            if (file.size > 5 * 1024 * 1024) {
                              alert('Файл слишком большой. Максимум 5MB');
                              e.target.value = '';
                              return;
                            }
                            const newFiles = [...createPhotoFiles];
                            newFiles[idx] = file;
                            setCreatePhotoFiles(newFiles);
                            const reader = new FileReader();
                            reader.onload = (ev) => {
                              const newPreviews = [...createPhotoPreviews];
                              newPreviews[idx] = ev.target.result;
                              setCreatePhotoPreviews(newPreviews);
                            };
                            reader.readAsDataURL(file);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => {
                setShowCreateModal(false);
                setCreatePhotoPreviews([null, null, null]);
                setCreatePhotoFiles([null, null, null]);
              }}>
                Отмена
              </button>
              <button 
                className="btn btn-primary" 
                onClick={createNews}
                disabled={saving}
              >
                {saving ? 'Сохранение...' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Direct Save Modal */}
      {showDirectSaveModal && (
        <div className="modal-overlay" onClick={() => setShowDirectSaveModal(false)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">💾 БД сохранение (без ИИ)</h2>
              <button className="modal-close" onClick={() => setShowDirectSaveModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p className="direct-save-hint">
                Загрузите уже готовые комиксы и баблы напрямую в базу данных
              </p>
              
              <div className="input-group">
                <label className="input-label">Заголовок *</label>
                <input
                  type="text"
                  className="input"
                  value={directForm.title}
                  onChange={(e) => setDirectForm({...directForm, title: e.target.value})}
                  placeholder="Заголовок новости"
                />
              </div>
              
              <div className="input-group">
                <label className="input-label">Статья *</label>
                <textarea
                  className="input textarea"
                  value={directForm.article}
                  onChange={(e) => setDirectForm({...directForm, article: e.target.value})}
                  placeholder="Текст статьи..."
                  rows={6}
                />
              </div>
              
              <div className="input-row">
                <div className="input-group">
                  <label className="input-label">Категория</label>
                  <input
                    type="text"
                    className="input"
                    value={directForm.category}
                    onChange={(e) => setDirectForm({...directForm, category: e.target.value})}
                  />
                </div>
                <div className="input-group">
                  <label className="input-label">URL источника</label>
                  <input
                    type="text"
                    className="input"
                    value={directForm.sourceUrl}
                    onChange={(e) => setDirectForm({...directForm, sourceUrl: e.target.value})}
                  />
                </div>
              </div>
              
              <div className="upload-section">
                <p className="upload-label">Загрузка файлов (комикс + баблы на 6 языках)</p>
                <div className="upload-hint">
                  Используйте форму на сервере или загрузите через API
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setShowDirectSaveModal(false)}>
                Отмена
              </button>
              <button className="btn btn-primary">
                <Save size={18} />
                Положить в БД
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Parser Settings Modal */}
      {showParserModal && (
        <div className="modal-overlay" onClick={() => setShowParserModal(false)}>
          <div className="modal modal-xl" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                <Settings size={24} />
                Настройки парсера
              </h2>
              <button className="modal-close" onClick={() => setShowParserModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              {/* Parser Controls */}
              <div className="parser-controls">
                <div className="parser-buttons">
                  {!parserRunning ? (
                    <button className="btn btn-success" onClick={startParser}>
                      <Play size={18} />
                      Запустить парсер
                    </button>
                  ) : (
                    <button className="btn btn-danger" onClick={stopParser}>
                      <Square size={18} />
                      Остановить
                    </button>
                  )}
                  <button className="btn btn-secondary" onClick={() => {
                    loadParserStatus();
                    loadParserLogs();
                    loadAutoParserSettings();
                  }}>
                    <RefreshCw size={18} />
                    Обновить
                  </button>
                </div>
              </div>

              {/* Parser Status */}
              <div className="parser-section">
                <h3 className="parser-section-title">Статус парсера</h3>
                <div className="parser-status-grid">
                  <div className="parser-stat">
                    <span className="parser-stat-label">Статус:</span>
                    <span className={`parser-stat-value ${parserStatus?.enabled ? 'text-success' : 'text-danger'}`}>
                      {parserStatus?.enabled ? '✓ Включен' : '✗ Выключен'}
                    </span>
                  </div>
                  <div className="parser-stat">
                    <span className="parser-stat-label">Процесс:</span>
                    <span className={`parser-stat-value ${parserRunning ? 'text-success' : 'text-muted'}`}>
                      {parserRunning ? '🟢 Работает' : '⚫ Остановлен'}
                    </span>
                  </div>
                  <div className="parser-stat">
                    <span className="parser-stat-label">Проверка дубликатов:</span>
                    <span className="parser-stat-value">
                      {parserStatus?.checkForDuplicates ? '✓ Включена' : 'Выключена'}
                    </span>
                  </div>
                  <div className="parser-stat">
                    <span className="parser-stat-label">Интервал:</span>
                    <span className="parser-stat-value">
                      {parserStatus?.checkInterval ? `${Math.floor(parserStatus.checkInterval / 3600)}ч ${Math.floor((parserStatus.checkInterval % 3600) / 60)}м` : '-'}
                    </span>
                  </div>
                  <div className="parser-stat">
                    <span className="parser-stat-label">Всего новостей:</span>
                    <span className="parser-stat-value">{parserStatus?.newsStats?.total || 0}</span>
                  </div>
                  <div className="parser-stat">
                    <span className="parser-stat-label">За 24ч:</span>
                    <span className="parser-stat-value">{parserStatus?.newsStats?.last24h || 0}</span>
                  </div>
                </div>
              </div>

              {/* Auto Parser */}
              <div className="parser-section">
                <h3 className="parser-section-title">Авто-парсер</h3>
                <div className="auto-parser-controls">
                  <button 
                    className={`btn ${autoParserSettings?.autoEnabled ? 'btn-danger' : 'btn-success'} btn-lg`}
                    onClick={toggleAutoParser}
                  >
                    <Power size={20} />
                    {autoParserSettings?.autoEnabled ? 'Выключить автопарсер' : 'Включить автопарсер'}
                  </button>
                </div>
                {autoParserSettings?.autoEnabled && (
                  <div className="auto-parser-info">
                    <p style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                      Режим: поддержка стэка
                      <input
                        type="number"
                        min="5"
                        max="400"
                        defaultValue={autoParserSettings.targetNewsCount || 30}
                        style={{width: '60px', padding: '2px 6px', borderRadius: '4px', border: '1px solid #555', background: '#2a2a3e', color: '#fff', textAlign: 'center', fontSize: '14px'}}
                        onBlur={(e) => {
                          const val = parseInt(e.target.value, 10);
                          if (val && val !== (autoParserSettings.targetNewsCount || 30)) {
                            updateStackSize(val);
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                      />
                      статей NEW
                    </p>
                    <p>Стэк NEW: <strong style={{color: (autoParserSettings.newCount || 0) >= (autoParserSettings.targetNewsCount || 30) ? '#10b981' : '#f59e0b'}}>{autoParserSettings.newCount || 0} / {autoParserSettings.targetNewsCount || 30}</strong></p>
                    <p>Проверка: каждые 5 минут</p>
                    {autoParserSettings.nextRun && (
                      <p>Следующая: {new Date(autoParserSettings.nextRun).toLocaleString('ru-RU')}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Parser Logs */}
              <div className="parser-section">
                <div className="parser-section-header">
                  <h3 className="parser-section-title">Логи парсера</h3>
                  <button className="btn btn-ghost btn-sm" onClick={clearParserLogs}>
                    <Trash2 size={16} />
                    Очистить
                  </button>
                </div>
                <div className="parser-logs">
                  {parserLogs.length === 0 ? (
                    <div className="parser-logs-empty">Нет логов</div>
                  ) : (
                    parserLogs.map((log, idx) => (
                      <div key={idx} className={`parser-log-item parser-log-${log.type || 'info'}`}>
                        <span className="parser-log-icon">
                          {log.type === 'success' && '✓'}
                          {log.type === 'error' && '✗'}
                          {log.type === 'warning' && '⚠'}
                          {log.type === 'duplicate' && '⚡'}
                          {log.type === 'info' && 'ℹ'}
                          {!log.type && '•'}
                        </span>
                        <div className="parser-log-content">
                          <div className="parser-log-message">{log.message}</div>
                          <div className="parser-log-time">
                            {new Date(log.timestamp).toLocaleString('ru-RU')}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Arizona Settings Modal */}
      {showArizonaModal && (
        <div className="modal-overlay" onClick={() => setShowArizonaModal(false)}>
          <div className="modal-content arizona-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">🎨 Настройки Arizona / BubbloGen</h2>
              <button className="modal-close" onClick={() => setShowArizonaModal(false)}><X size={20} /></button>
            </div>

            {arizonaStatus && (
              <div className={`arizona-status ${arizonaStatus.startsWith('✅') ? 'success' : 'error'}`}>
                {arizonaStatus}
              </div>
            )}

            {arizonaSettings ? (
              <>
                {/* Tabs */}
                <div className="arizona-tabs">
                  <button 
                    className={`arizona-tab ${arizonaTab === 'settings' ? 'active' : ''}`}
                    onClick={() => setArizonaTab('settings')}
                  >
                    ⚙️ Настройки
                  </button>
                  <button 
                    className={`arizona-tab ${arizonaTab === 'prompt1' ? 'active' : ''}`}
                    onClick={() => setArizonaTab('prompt1')}
                  >
                    📝 Prompt 1-2 (Анализ)
                  </button>
                  <button 
                    className={`arizona-tab ${arizonaTab === 'prompt2' ? 'active' : ''}`}
                    onClick={() => setArizonaTab('prompt2')}
                  >
                    🖼️ Prompt 2 (Картинка)
                  </button>
                  <button 
                    className={`arizona-tab ${arizonaTab === 'support' ? 'active' : ''}`}
                    onClick={() => setArizonaTab('support')}
                  >
                    💬 Поддержка
                  </button>
                </div>

                {/* Tab: Settings */}
                {arizonaTab === 'settings' && (
                <>
                <div className="arizona-grid">
                  {/* Column 1: TEXT + FONT */}
                  <div className="arizona-col">
                    <h4 className="arizona-section-title">TEXT</h4>
                    <label className="az-label">MAX_CHARS</label>
                    <input type="number" className="input az-input" value={arizonaSettings.MAX_CHARS || ''} onChange={e => updateAz('MAX_CHARS', parseInt(e.target.value))} />
                    <label className="az-label">LINE_BREAK_1</label>
                    <input type="number" className="input az-input" value={arizonaSettings.LINE_BREAK_1 || ''} onChange={e => updateAz('LINE_BREAK_1', parseInt(e.target.value))} />
                    <label className="az-label">LINE_BREAK_2</label>
                    <input type="number" className="input az-input" value={arizonaSettings.LINE_BREAK_2 || ''} onChange={e => updateAz('LINE_BREAK_2', parseInt(e.target.value))} />

                    <h4 className="arizona-section-title" style={{marginTop: '15px'}}>FONT</h4>
                    <label className="az-label">FONT_PATH</label>
                    <input type="text" className="input az-input" value={arizonaSettings.FONT_PATH || ''} onChange={e => updateAz('FONT_PATH', e.target.value)} />
                    <label className="az-label">FONT_SIZE</label>
                    <input type="number" className="input az-input" value={arizonaSettings.FONT_SIZE || ''} onChange={e => updateAz('FONT_SIZE', parseInt(e.target.value))} />
                    <label className="az-label">FONT_COLOR (R,G,B)</label>
                    <input type="text" className="input az-input" value={rgbToStr(arizonaSettings.FONT_COLOR)} onChange={e => updateAz('FONT_COLOR', strToRgb(e.target.value))} placeholder="37,22,10" />
                    <label className="az-label">JP_FONT_PATH</label>
                    <input type="text" className="input az-input" value={arizonaSettings.JP_FONT_PATH || ''} onChange={e => updateAz('JP_FONT_PATH', e.target.value)} />
                  </div>

                  {/* Column 2: BUBBLE SHAPE + LAYOUT */}
                  <div className="arizona-col">
                    <h4 className="arizona-section-title">BUBBLE SHAPE</h4>
                    <label className="az-label">BUBBLE_COLOR (R,G,B)</label>
                    <input type="text" className="input az-input" value={rgbToStr(arizonaSettings.BUBBLE_COLOR)} onChange={e => updateAz('BUBBLE_COLOR', strToRgb(e.target.value))} placeholder="255,255,255" />
                    <label className="az-label">BUBBLE_ALPHA (0-1)</label>
                    <input type="number" step="0.05" className="input az-input" value={arizonaSettings.BUBBLE_ALPHA ?? ''} onChange={e => updateAz('BUBBLE_ALPHA', parseFloat(e.target.value))} />
                    <label className="az-label">BUBBLE_BORDER_COLOR (R,G,B)</label>
                    <input type="text" className="input az-input" value={rgbToStr(arizonaSettings.BUBBLE_BORDER_COLOR)} onChange={e => updateAz('BUBBLE_BORDER_COLOR', strToRgb(e.target.value))} placeholder="37,22,10" />
                    <label className="az-label">BUBBLE_BORDER_THICKNESS</label>
                    <input type="number" className="input az-input" value={arizonaSettings.BUBBLE_BORDER_THICKNESS || ''} onChange={e => updateAz('BUBBLE_BORDER_THICKNESS', parseInt(e.target.value))} />
                    <label className="az-label">BUBBLE_FLATNESS</label>
                    <input type="number" step="0.05" className="input az-input" value={arizonaSettings.BUBBLE_FLATNESS ?? ''} onChange={e => updateAz('BUBBLE_FLATNESS', parseFloat(e.target.value))} />

                    <h4 className="arizona-section-title" style={{marginTop: '15px'}}>LAYOUT / GRID</h4>
                    <label className="az-label">IMAGE_SIZE</label>
                    <input type="number" className="input az-input" value={arizonaSettings.IMAGE_SIZE || ''} onChange={e => updateAz('IMAGE_SIZE', parseInt(e.target.value))} />
                    <label className="az-label">GRID_ROWS</label>
                    <input type="number" className="input az-input" value={arizonaSettings.GRID_ROWS || ''} onChange={e => updateAz('GRID_ROWS', parseInt(e.target.value))} />
                    <label className="az-label">GRID_COLS</label>
                    <input type="number" className="input az-input" value={arizonaSettings.GRID_COLS || ''} onChange={e => updateAz('GRID_COLS', parseInt(e.target.value))} />
                    <label className="az-label">FORBIDDEN_ZONES (row,col;...)</label>
                    <input type="text" className="input az-input" value={fzToStr(arizonaSettings.FORBIDDEN_ZONES)} onChange={e => updateAz('FORBIDDEN_ZONES', strToFz(e.target.value))} placeholder="0,1;1,1" />
                  </div>

                  {/* Column 3: PLACEMENT + FACE + BALLETPOST */}
                  <div className="arizona-col">
                    <h4 className="arizona-section-title">PLACEMENT</h4>
                    <label className="az-label">MAX_ITER_ZONE</label>
                    <input type="number" className="input az-input" value={arizonaSettings.MAX_ITER_ZONE || ''} onChange={e => updateAz('MAX_ITER_ZONE', parseInt(e.target.value))} />
                    <label className="az-label">MAX_ITER_BUBBLES</label>
                    <input type="number" className="input az-input" value={arizonaSettings.MAX_ITER_BUBBLES || ''} onChange={e => updateAz('MAX_ITER_BUBBLES', parseInt(e.target.value))} />
                    <label className="az-label">MAX_PLACEMENT_ITER</label>
                    <input type="number" className="input az-input" value={arizonaSettings.MAX_PLACEMENT_ITER || ''} onChange={e => updateAz('MAX_PLACEMENT_ITER', parseInt(e.target.value))} />
                    <label className="az-label">MIN_BUBBLE_MARGIN</label>
                    <input type="number" className="input az-input" value={arizonaSettings.MIN_BUBBLE_MARGIN || ''} onChange={e => updateAz('MIN_BUBBLE_MARGIN', parseInt(e.target.value))} />

                    <h4 className="arizona-section-title" style={{marginTop: '15px'}}>FACE DETECTION</h4>
                    <label className="az-label">USE_DNN_FACE</label>
                    <select className="input az-input" value={arizonaSettings.USE_DNN_FACE ? 'true' : 'false'} onChange={e => updateAz('USE_DNN_FACE', e.target.value === 'true')}>
                      <option value="true">True</option>
                      <option value="false">False</option>
                    </select>
                    <label className="az-label">FACE_PADDING_X</label>
                    <input type="number" className="input az-input" value={arizonaSettings.FACE_PADDING_X || ''} onChange={e => updateAz('FACE_PADDING_X', parseInt(e.target.value))} />
                    <label className="az-label">FACE_PADDING_Y</label>
                    <input type="number" className="input az-input" value={arizonaSettings.FACE_PADDING_Y || ''} onChange={e => updateAz('FACE_PADDING_Y', parseInt(e.target.value))} />
                    <label className="az-label">EXTRA_FACE_MARGIN</label>
                    <input type="number" className="input az-input" value={arizonaSettings.EXTRA_FACE_MARGIN || ''} onChange={e => updateAz('EXTRA_FACE_MARGIN', parseInt(e.target.value))} />

                    <h4 className="arizona-section-title" style={{marginTop: '15px'}}>BALLETPOST.PY</h4>
                    <label className="az-label">TEXTMODEL</label>
                    <input type="text" className="input az-input" value={arizonaSettings.TEXTMODEL || ''} onChange={e => updateAz('TEXTMODEL', e.target.value)} />
                    <label className="az-label">IMAGEMODEL</label>
                    <input type="text" className="input az-input" value={arizonaSettings.IMAGEMODEL || ''} onChange={e => updateAz('IMAGEMODEL', e.target.value)} />
                    <label className="az-label">LANGORDER (через запятую)</label>
                    <input type="text" className="input az-input" value={Array.isArray(arizonaSettings.LANGORDER) ? arizonaSettings.LANGORDER.join(',') : ''} onChange={e => updateAz('LANGORDER', e.target.value.split(',').map(s => s.trim()).filter(Boolean))} placeholder="RU,EN,ES,IT,JA,DE" />
                  </div>
                </div>

                {/* Timer Settings */}
                <div className="arizona-timers">
                  <h4 className="arizona-section-title">ТАЙМЕРЫ СТАТУСОВ (часы)</h4>
                  <div className="arizona-timer-grid">
                    <div>
                      <label className="az-label">T1: new → good (авто-одобрение)</label>
                      <input type="number" className="input az-input" value={timerSettings.t1_hours || ''} onChange={e => setTimerSettings(p => ({...p, t1_hours: parseInt(e.target.value)}))} />
                    </div>
                    <div>
                      <label className="az-label">T2: good → КомДел (авто-обработка)</label>
                      <input type="number" className="input az-input" value={timerSettings.t2_hours || ''} onChange={e => setTimerSettings(p => ({...p, t2_hours: parseInt(e.target.value)}))} />
                    </div>
                    <div>
                      <label className="az-label">T3: ready → posted (авто-публикация)</label>
                      <input type="number" className="input az-input" value={timerSettings.t3_hours || ''} onChange={e => setTimerSettings(p => ({...p, t3_hours: parseInt(e.target.value)}))} />
                    </div>
                  </div>
                </div>

                {/* Miniapp Desktop Size */}
                <div className="arizona-timers" style={{marginTop: '20px'}}>
                  <h4 className="arizona-section-title">📱 РАЗМЕР МИНИ-ПРИЛОЖЕНИЯ (только ПК)</h4>
                  <p style={{fontSize: '11px', color: 'var(--text-muted)', margin: '0 0 10px 0'}}>На телефонах всегда авто (100%). Укажите 0 для авто-размера на ПК.</p>
                  <div className="arizona-timer-grid" style={{gridTemplateColumns: '1fr 1fr'}}>
                    <div>
                      <label className="az-label">Ширина (px)</label>
                      <input type="number" className="input az-input" min="0" max="3000" placeholder="0 = авто" value={miniappSize.screenWidth || ''} onChange={e => setMiniappSize(p => ({...p, screenWidth: parseInt(e.target.value) || 0}))} />
                    </div>
                    <div>
                      <label className="az-label">Высота (px)</label>
                      <input type="number" className="input az-input" min="0" max="3000" placeholder="0 = авто" value={miniappSize.screenHeight || ''} onChange={e => setMiniappSize(p => ({...p, screenHeight: parseInt(e.target.value) || 0}))} />
                    </div>
                  </div>
                </div>
                </>
                )}

                {/* Tab: Prompt 1-2 (Text Analysis) */}
                {arizonaTab === 'prompt1' && (
                  <div className="arizona-prompt-tab">
                    <div className="arizona-prompt-info">
                      <strong>Prompt_1-2.txt</strong> — системный промпт для GPT-4.1 (анализ текста, генерация JSON с VIDEO_TASK, VISUAL_PARAMETERS, MULTILANG_CONTENT)
                    </div>
                    {!arizonaPrompts.prompt_1_2 && (
                      <div className="arizona-prompt-empty">
                        ⚠️ Промпт пуст. Файл <code>Arizona/Prompts/Prompt_1-2.txt</code> не найден на сервере или пустой. Введите промпт ниже и нажмите «Сохранить всё».
                      </div>
                    )}
                    <textarea 
                      className="arizona-prompt-editor"
                      value={arizonaPrompts.prompt_1_2 || ''}
                      onChange={e => setArizonaPrompts(p => ({...p, prompt_1_2: e.target.value}))}
                      spellCheck={false}
                      placeholder="Вставьте промпт для анализа текста..."
                    />
                    <div className="arizona-prompt-meta">
                      {(arizonaPrompts.prompt_1_2 || '').length} символов · {(arizonaPrompts.prompt_1_2 || '').split('\n').length} строк
                    </div>
                  </div>
                )}

                {/* Tab: Prompt 2 (Image Generation) */}
                {arizonaTab === 'prompt2' && (
                  <div className="arizona-prompt-tab">
                    <div className="arizona-prompt-info">
                      <strong>Prompt_2.txt</strong> — промпт для генерации изображения (gpt-image-1.5). Стиль: 1950s mid-century comic book.
                    </div>
                    {!arizonaPrompts.prompt_2 && (
                      <div className="arizona-prompt-empty">
                        ⚠️ Промпт пуст. Файл <code>Arizona/Prompts/Prompt_2.txt</code> не найден на сервере или пустой. Введите промпт ниже и нажмите «Сохранить всё».
                      </div>
                    )}
                    <textarea 
                      className="arizona-prompt-editor"
                      value={arizonaPrompts.prompt_2 || ''}
                      onChange={e => setArizonaPrompts(p => ({...p, prompt_2: e.target.value}))}
                      spellCheck={false}
                      placeholder="Вставьте промпт для генерации изображения..."
                    />
                    <div className="arizona-prompt-meta">
                      {(arizonaPrompts.prompt_2 || '').length} символов · {(arizonaPrompts.prompt_2 || '').split('\n').length} строк
                    </div>
                  </div>
                )}

                {/* Tab: Support Chat Prompt */}
                {arizonaTab === 'support' && (
                  <div className="arizona-prompt-tab">
                    <div className="arizona-prompt-info">
                      <strong>Support_Chat.txt</strong> — системный промпт для чата поддержки на лендинге. Определяет поведение и знания ИИ-консультанта.
                    </div>
                    {!arizonaPrompts.support_chat && (
                      <div className="arizona-prompt-empty">
                        ⚠️ Промпт пуст. Файл <code>Arizona/Prompts/Support_Chat.txt</code> не найден на сервере или пустой. Введите промпт ниже и нажмите «Сохранить всё».
                      </div>
                    )}
                    <textarea 
                      className="arizona-prompt-editor"
                      value={arizonaPrompts.support_chat || ''}
                      onChange={e => setArizonaPrompts(p => ({...p, support_chat: e.target.value}))}
                      spellCheck={false}
                      placeholder="Вставьте системный промпт для чата поддержки..."
                    />
                    <div className="arizona-prompt-meta">
                      {(arizonaPrompts.support_chat || '').length} символов · {(arizonaPrompts.support_chat || '').split('\n').length} строк
                    </div>
                  </div>
                )}

                <div className="arizona-actions">
                  <button className="btn btn-secondary" onClick={() => setShowArizonaModal(false)}>Отмена</button>
                  <button className="btn btn-arizona" onClick={saveArizonaSettings} disabled={arizonaSaving}>
                    {arizonaSaving ? <><Loader size={16} className="spin" /> Сохранение...</> : '💾 Сохранить'}
                  </button>
                </div>
              </>
            ) : (
              <div style={{textAlign: 'center', padding: '40px', color: 'var(--text-muted)'}}>
                <Loader size={24} className="spin" /> Загрузка настроек...
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default News;
